import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/productSearch"
import { SALES_SEARCH_LIMIT, SUGGESTION_DEBOUNCE_MS } from "../constants"
import type { SalesProduct } from "../types"

import { ComputedProductStatus, GetSalesProductListDocument } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

interface UseGetSalesProductSuggestionsReturn {
  suggestions: SalesProduct[]
}

export const useGetSalesProductSuggestions = (
  searchInputValue: string,
  excludedProductIds: Set<number>
): UseGetSalesProductSuggestionsReturn => {
  const debouncedQuery = useDebounce(searchInputValue.trim(), SUGGESTION_DEBOUNCE_MS)

  const [{ data }] = useQuery({
    query: GetSalesProductListDocument,
    variables: {
      input: {
        filter: {
          query: debouncedQuery,
          computedProductStatuses: [ComputedProductStatus.Active],
        },
        limit: SALES_SEARCH_LIMIT,
        offset: 0,
      },
    },
    pause: debouncedQuery.length < 2,
  })

  const suggestions = useMemo<SalesProduct[]>(() => {
    if (data?.product?.list?.__typename !== "ProductListSuccess") return []
    return data.product.list.products.filter((product) => !excludedProductIds.has(product.productId))
  }, [data, excludedProductIds])

  return { suggestions }
}
